import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  EmployeeCreateBodyParams,
  EmployeeUpdateBodyParams,
  TCEmployee,
} from 'src/api/generated';
import { EmployeePersonalityCommunicationStyle } from 'src/api/generated/models/EmployeePersonalityCommunicationStyle';
import { useState, useMemo, useEffect } from 'react';
import { Tabs, TabsHeader, Tab } from '@material-tailwind/react';
import { EmployeeOverviewTab } from './tabs/overview-tab';
import { EmployeeToolsTab } from './tabs/tools-tab';
import { EmployeeWorkspaceTab } from './tabs/workspace-tab';
import {
  EmployeeFormInputValues,
  EmployeeFormValues,
  EmployeeFormPath,
  TabValue,
  employeeFormSchema,
} from './types';
import { sanitizeEmployeeDefinitionTools } from 'src/modules/automations/utils/employees/employee-form.util';
import { Text } from 'src/modules/shared/components/atoms/text';
import { PiBrainLight, PiWrenchLight, PiGlobeLight } from 'react-icons/pi';

interface EmployeeFormProps {
  employee?: TCEmployee;
  onSubmit: (data: EmployeeCreateBodyParams | EmployeeUpdateBodyParams) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  isEdit?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}

export const EmployeeForm: React.FC<EmployeeFormProps> = ({
  employee,
  onSubmit,
  onDirtyChange,
}) => {
  const [activeTab, setActiveTab] = useState<TabValue>('overview');

  const initialObjective =
    employee?.draftVersion?.definition?.objectives?.[0] || '';

  const form = useForm<EmployeeFormInputValues, unknown, EmployeeFormValues>({
    resolver: zodResolver(employeeFormSchema),
    defaultValues: {
      metadata: {
        name: employee?.metadata?.name || '',
        description: employee?.metadata?.description || '',
      },
      definition: {
        jobTitle: employee?.draftVersion?.definition?.jobTitle || '',
        tools: employee?.draftVersion?.definition?.tools || [],
        objectives: employee?.draftVersion?.definition?.objectives || [],
        constraints: employee?.draftVersion?.definition?.constraints || [],
        personality: {
          communicationStyle:
            employee?.draftVersion?.definition?.personality
              ?.communicationStyle ||
            EmployeePersonalityCommunicationStyle.PROFESSIONAL,
          name: employee?.draftVersion?.definition?.personality?.name || '',
          customTraits:
            employee?.draftVersion?.definition?.personality?.customTraits || '',
        },
        knowledgeBase: employee?.draftVersion?.definition?.knowledgeBase || '',
      },
    },
  });

  const {
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = form;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const configuredToolsCount = useMemo(() => {
    const employeeTools = watch(EmployeeFormPath.DEFINITION_TOOLS) || [];
    return employeeTools.length;
  }, [watch]);

  const handleFormSubmit = (data: EmployeeFormValues) => {
    const { metadata, definition } = data;

    onSubmit({
      metadata,
      definition: {
        ...sanitizeEmployeeDefinitionTools(definition),
      },
    });
  };

  const handleFormInvalid = () => {
    // Auto-focus tab with first error to guide the user
    if (errors?.definition?.tools) {
      setActiveTab('tools');
      return;
    }
    setActiveTab('overview');
  };

  return (
    <form
      id="employee-form"
      onSubmit={handleSubmit(handleFormSubmit, handleFormInvalid)}
      className="space-y-8 px-6 py-6"
    >
      <Tabs value={activeTab} className="overflow-visible">
        <TabsHeader className="border border-secondary-200">
          <Tab
            key="overview"
            value="overview"
            onClick={() => setActiveTab('overview')}
            className="font-medium"
          >
            <div className="flex items-center gap-2">
              <PiBrainLight className="h-5 w-5" />
              <Text size="h6" weight="medium">
                {/* TODO: check when implementing i18n */}
                Comportamiento
              </Text>
            </div>
          </Tab>
          <Tab
            key="tools"
            value="tools"
            onClick={() => setActiveTab('tools')}
            className="font-medium"
          >
            <div className="flex items-center gap-2">
              <PiWrenchLight className="h-5 w-5" />

              <Text size="h6" weight="medium">
                {/* TODO: check when implementing i18n */}
                Herramientas
              </Text>

              {configuredToolsCount > 0 && (
                <span className="bg-primary-500 text-white rounded-full text-xs px-2 py-0.5">
                  {configuredToolsCount}
                </span>
              )}
            </div>
          </Tab>
          <Tab
            key="workspace"
            value="workspace"
            onClick={() => setActiveTab('workspace')}
            className="font-medium"
          >
            {/* TODO: check when implementing i18n */}
            <div className="flex items-center gap-2">
              <PiGlobeLight className="h-5 w-5" />
              <Text size="h6" weight="medium">
                Espacio de trabajo
              </Text>
            </div>
          </Tab>
        </TabsHeader>
      </Tabs>

      {activeTab === 'overview' && (
        <EmployeeOverviewTab form={form} initialObjective={initialObjective} />
      )}
      {activeTab === 'tools' && <EmployeeToolsTab form={form} />}
      {activeTab === 'workspace' && (
        <EmployeeWorkspaceTab
          employeeId={employee?.metadata?.employeeId || ''}
          version={employee?.draftVersion?.version ?? 'DRAFT'}
        />
      )}
    </form>
  );
};
